// PDF builders for the Bill History page:
//  - exportSalesSummaryPdf : designed sales summary (KPI cards, table, charts)
//  - exportInvoicesPdf     : many invoices rendered into ONE pdf file (one invoice per page)
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";
import { parseISO, format, isValid } from "date-fns";

const COLORS = {
  navy: [31, 42, 68],
  accent: [235, 115, 10],
  blue: [37, 99, 235],
  green: [22, 163, 74],
  amber: [245, 158, 11],
  text: [51, 65, 85],
  muted: [100, 116, 139],
  line: [226, 232, 240],
  soft: [248, 250, 252],
};
const PALETTE = [
  [37, 99, 235],
  [22, 163, 74],
  [245, 158, 11],
  [139, 92, 246],
  [6, 182, 212],
  [236, 72, 153],
  [100, 116, 139],
];

const num = (v) => parseFloat(v) || 0;
const money = (v) =>
  num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compact = (v) => {
  const n = Math.abs(v);
  if (n >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return v.toFixed(0);
};
const billDate = (b) => {
  const raw = b.setup_date || b.inv_date;
  if (!raw) return null;
  const d = typeof raw === "string" ? parseISO(raw) : new Date(raw);
  return isValid(d) ? d : null;
};

// Rounds the top of an axis to a "nice" number and returns ticks.
const niceScale = (max, steps = 5) => {
  if (max <= 0) return { max: 1, ticks: [0, 1] };
  const rough = max / steps;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const mult = [1, 2, 2.5, 5, 10].find((m) => m * pow >= rough) * pow;
  const top = Math.ceil(max / mult) * mult;
  const ticks = [];
  for (let v = 0; v <= top + mult / 2; v += mult) ticks.push(v);
  return { max: top, ticks };
};

// ---- drawing primitives -------------------------------------------------
const fill = (doc, c) => doc.setFillColor(c[0], c[1], c[2]);
const stroke = (doc, c) => doc.setDrawColor(c[0], c[1], c[2]);
const ink = (doc, c) => doc.setTextColor(c[0], c[1], c[2]);

const polygon = (doc, pts, style = "F") => {
  const rel = [];
  for (let i = 1; i < pts.length; i++) {
    rel.push([pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]]);
  }
  doc.lines(rel, pts[0][0], pts[0][1], [1, 1], style, true);
};

const sectionTitle = (doc, text, x, y, w) => {
  fill(doc, COLORS.accent);
  doc.rect(x, y - 4, 1.6, 6, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  ink(doc, COLORS.navy);
  doc.text(text, x + 4, y);
  stroke(doc, COLORS.line);
  doc.setLineWidth(0.2);
  doc.line(x, y + 3, x + w, y + 3);
};

const card = (doc, x, y, w, h, label, value, color) => {
  fill(doc, COLORS.soft);
  stroke(doc, COLORS.line);
  doc.setLineWidth(0.2);
  doc.roundedRect(x, y, w, h, 2, 2, "FD");
  fill(doc, color);
  doc.roundedRect(x, y, 1.8, h, 0.9, 0.9, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  ink(doc, COLORS.muted);
  doc.text(label.toUpperCase(), x + 5, y + 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  ink(doc, color);
  doc.text(value, x + 5, y + 14);
};

// Vertical bar chart with grid, y-axis ticks, value labels.
const barChart = (doc, { x, y, w, h, data, color = COLORS.blue, title }) => {
  sectionTitle(doc, title, x, y, w);
  const top = y + 10;
  const padL = 16;
  const padB = 12;
  const cw = w - padL;
  const ch = h - 10 - padB;
  if (!data.length) {
    doc.setFontSize(9);
    ink(doc, COLORS.muted);
    doc.text("No data", x + w / 2, top + ch / 2, { align: "center" });
    return;
  }
  const scale = niceScale(Math.max(...data.map((d) => d.value)));
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  scale.ticks.forEach((t) => {
    const ty = top + ch - (t / scale.max) * ch;
    stroke(doc, COLORS.line);
    doc.setLineWidth(0.15);
    doc.line(x + padL, ty, x + w, ty);
    ink(doc, COLORS.muted);
    doc.text(compact(t), x + padL - 2, ty + 1, { align: "right" });
  });
  const slot = cw / data.length;
  const bw = Math.min(slot * 0.6, 18);
  data.forEach((d, i) => {
    const bh = (d.value / scale.max) * ch;
    const bx = x + padL + slot * i + (slot - bw) / 2;
    const by = top + ch - bh;
    fill(doc, color);
    doc.roundedRect(bx, by, bw, Math.max(bh, 0.3), 0.8, 0.8, "F");
    doc.setFontSize(6.5);
    ink(doc, COLORS.text);
    doc.text(compact(d.value), bx + bw / 2, by - 1.2, { align: "center" });
    ink(doc, COLORS.muted);
    doc.setFontSize(7);
    doc.text(String(d.label), bx + bw / 2, top + ch + 5, { align: "center" });
    if (d.sub) {
      doc.setFontSize(6);
      doc.text(d.sub, bx + bw / 2, top + ch + 8.5, { align: "center" });
    }
  });
  stroke(doc, COLORS.muted);
  doc.setLineWidth(0.3);
  doc.line(x + padL, top + ch, x + w, top + ch);
};

// Area/line chart.
const lineChart = (doc, { x, y, w, h, data, color = COLORS.green, title }) => {
  sectionTitle(doc, title, x, y, w);
  const top = y + 10;
  const padL = 16;
  const padB = 8;
  const cw = w - padL - 3;
  const ch = h - 10 - padB;
  if (data.length < 1) {
    doc.setFontSize(9);
    ink(doc, COLORS.muted);
    doc.text("No data", x + w / 2, top + ch / 2, { align: "center" });
    return;
  }
  const scale = niceScale(Math.max(...data.map((d) => d.value)), 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  scale.ticks.forEach((t) => {
    const ty = top + ch - (t / scale.max) * ch;
    stroke(doc, COLORS.line);
    doc.setLineWidth(0.15);
    doc.line(x + padL, ty, x + w, ty);
    ink(doc, COLORS.muted);
    doc.text(compact(t), x + padL - 2, ty + 1, { align: "right" });
  });
  const px = (i) => x + padL + (data.length === 1 ? cw / 2 : (cw * i) / (data.length - 1));
  const py = (v) => top + ch - (v / scale.max) * ch;
  const pts = data.map((d, i) => [px(i), py(d.value)]);
  if (pts.length > 1) {
    doc.setGState(new doc.GState({ opacity: 0.15 }));
    fill(doc, color);
    polygon(doc, [[pts[0][0], top + ch], ...pts, [pts[pts.length - 1][0], top + ch]], "F");
    doc.setGState(new doc.GState({ opacity: 1 }));
    stroke(doc, color);
    doc.setLineWidth(0.6);
    for (let i = 1; i < pts.length; i++) {
      doc.line(pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1]);
    }
  }
  fill(doc, color);
  pts.forEach((p) => doc.circle(p[0], p[1], data.length > 40 ? 0.4 : 0.9, "F"));
  // x labels (max ~8)
  const every = Math.max(1, Math.ceil(data.length / 8));
  doc.setFontSize(6.5);
  ink(doc, COLORS.muted);
  data.forEach((d, i) => {
    if (i % every === 0 || i === data.length - 1) {
      doc.text(String(d.label), px(i), top + ch + 5, { align: "center" });
    }
  });
  // peak marker
  const peak = data.reduce((m, d, i) => (d.value > data[m].value ? i : m), 0);
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  ink(doc, color);
  doc.text(`Peak ${compact(data[peak].value)}`, px(peak), py(data[peak].value) - 2, {
    align: "center",
  });
  stroke(doc, COLORS.muted);
  doc.setLineWidth(0.3);
  doc.line(x + padL, top + ch, x + w, top + ch);
};

// Donut chart + legend table.
const donutChart = (doc, { x, y, w, h, slices, title, total, nameHead = "MODE", countHead = "BILLS" }) => {
  sectionTitle(doc, title, x, y, w);
  const cx = x + 30;
  const cy = y + 10 + (h - 10) / 2;
  const R = 24;
  const r = 14;
  const sum = slices.reduce((s, d) => s + d.value, 0);
  if (!sum) {
    doc.setFontSize(9);
    ink(doc, COLORS.muted);
    doc.text("No data", x + w / 2, cy, { align: "center" });
    return;
  }
  let a0 = -Math.PI / 2;
  slices.forEach((s, i) => {
    const a1 = a0 + (s.value / sum) * Math.PI * 2;
    const n = Math.max(4, Math.ceil(((a1 - a0) / (Math.PI * 2)) * 60));
    const outer = [];
    const inner = [];
    for (let k = 0; k <= n; k++) {
      const a = a0 + ((a1 - a0) * k) / n;
      outer.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
      inner.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    fill(doc, PALETTE[i % PALETTE.length]);
    stroke(doc, [255, 255, 255]);
    doc.setLineWidth(0.4);
    polygon(doc, [...outer, ...inner.reverse()], "FD");
    a0 = a1;
  });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  ink(doc, COLORS.navy);
  doc.text(compact(total), cx, cy + 0.5, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  ink(doc, COLORS.muted);
  doc.text("TOTAL", cx, cy + 4, { align: "center" });

  // legend
  const lx = x + 62;
  let ly = y + 16;
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  ink(doc, COLORS.muted);
  doc.text(nameHead, lx + 5, ly);
  doc.text(countHead, lx + 38, ly, { align: "right" });
  doc.text("AMOUNT", lx + 60, ly, { align: "right" });
  doc.text("%", lx + 72, ly, { align: "right" });
  ly += 5;
  slices.forEach((s, i) => {
    fill(doc, PALETTE[i % PALETTE.length]);
    doc.roundedRect(lx, ly - 2.6, 3, 3, 0.6, 0.6, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    ink(doc, COLORS.text);
    doc.text(String(s.label), lx + 5, ly);
    doc.text(String(s.count), lx + 38, ly, { align: "right" });
    doc.text(compact(s.value), lx + 60, ly, { align: "right" });
    doc.text(`${((s.value / sum) * 100).toFixed(1)}%`, lx + 72, ly, { align: "right" });
    ly += 6;
  });
};

// ---- aggregation --------------------------------------------------------
const aggregate = (bills) => {
  const monthly = {};
  const daily = {};
  const modes = {};
  bills.forEach((b) => {
    const d = billDate(b);
    const total = num(b.grand_total);
    if (d) {
      const mk = format(d, "yyyy-MM");
      monthly[mk] = monthly[mk] || { value: 0, count: 0, date: d };
      monthly[mk].value += total;
      monthly[mk].count += 1;
      const dk = format(d, "yyyy-MM-dd");
      daily[dk] = daily[dk] || { value: 0, date: d };
      daily[dk].value += total;
    }
    const mode = b.payment_mode || "N/A";
    modes[mode] = modes[mode] || { value: 0, count: 0 };
    modes[mode].value += total;
    modes[mode].count += 1;
  });
  return {
    monthly: Object.keys(monthly)
      .sort()
      .map((k) => ({
        label: format(monthly[k].date, "MMM"),
        sub: format(monthly[k].date, "yyyy"),
        value: monthly[k].value,
        count: monthly[k].count,
      })),
    daily: Object.keys(daily)
      .sort()
      .map((k) => ({ label: format(daily[k].date, "dd MMM"), value: daily[k].value })),
    modes: Object.entries(modes)
      .map(([label, v]) => ({ label, ...v }))
      .sort((a, b) => b.value - a.value),
  };
};

// ---- public: sales summary ---------------------------------------------
export const exportSalesSummaryPdf = ({
  bills,
  logo,
  companyName = "StoreHub",
  periodLabel = "All dates",
  statusLabel = "Active Bills",
  fileName = "salereport.pdf",
}) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 12;
  const CW = PW - M * 2;

  const totals = bills.reduce(
    (a, b) => {
      a.sub += num(b.subtotal_afterdiscount);
      a.tax += num(b.tax);
      a.grand += num(b.grand_total);
      return a;
    },
    { sub: 0, tax: 0, grand: 0 }
  );
  const agg = aggregate(bills);

  // header band
  fill(doc, COLORS.navy);
  doc.rect(0, 0, PW, 32, "F");
  fill(doc, COLORS.accent);
  doc.rect(0, 32, PW, 1.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(19);
  ink(doc, [255, 255, 255]);
  doc.text("Sales Report", M, 15);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  ink(doc, [203, 213, 225]);
  doc.text(`${companyName}  |  ${statusLabel}`, M, 22);
  doc.text(`Period: ${periodLabel}`, M, 27);
  doc.text(`Generated: ${format(new Date(), "dd MMM yyyy, HH:mm")}`, PW - M, 27, {
    align: "right",
  });
  if (logo) {
    try {
      fill(doc, [255, 255, 255]);
      doc.roundedRect(PW - M - 36, 5, 36, 14, 1.5, 1.5, "F");
      doc.addImage(logo, "PNG", PW - M - 34, 6, 32, 12);
    } catch (e) {
      // logo is optional
    }
  }

  // KPI cards
  const cw = (CW - 4 * 3) / 5;
  const kpis = [
    ["Total Bills", String(bills.length), COLORS.navy],
    ["Subtotal", money(totals.sub), COLORS.blue],
    ["Tax", money(totals.tax), COLORS.amber],
    ["Grand Total", money(totals.grand), COLORS.green],
    ["Avg / Bill", money(bills.length ? totals.grand / bills.length : 0), COLORS.accent],
  ];
  kpis.forEach((k, i) => card(doc, M + i * (cw + 3), 40, cw, 19, k[0], k[1], k[2]));

  // table
  const body = bills.map((b, i) => [
    i + 1,
    `#${b.id}`,
    b.setup_date || b.inv_date || "-",
    b.inv_time || "-",
    money(b.subtotal_afterdiscount),
    money(b.tax),
    money(b.grand_total),
    b.payment_mode || "-",
  ]);
  doc.autoTable({
    startY: 66,
    margin: { left: M, right: M, bottom: 16 },
    head: [["#", "Invoice", "Date", "Time", "Subtotal", "Tax", "Grand Total", "Payment"]],
    body,
    foot: [
      [
        { content: "TOTAL", colSpan: 4, styles: { halign: "right" } },
        money(totals.sub),
        money(totals.tax),
        money(totals.grand),
        "",
      ],
    ],
    showFoot: "lastPage",
    theme: "striped",
    styles: { fontSize: 8, cellPadding: 2, textColor: COLORS.text, lineColor: COLORS.line },
    headStyles: { fillColor: COLORS.navy, textColor: 255, fontStyle: "bold", halign: "center" },
    footStyles: { fillColor: COLORS.accent, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: COLORS.soft },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      1: { halign: "center", fontStyle: "bold" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "right" },
      5: { halign: "right" },
      6: { halign: "right", fontStyle: "bold", textColor: COLORS.green },
      7: { halign: "center" },
    },
  });

  // analytics page
  doc.addPage();
  fill(doc, COLORS.navy);
  doc.rect(0, 0, PW, 20, "F");
  fill(doc, COLORS.accent);
  doc.rect(0, 20, PW, 1.2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  ink(doc, [255, 255, 255]);
  doc.text("Summary & Analytics", M, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  ink(doc, [203, 213, 225]);
  doc.text(periodLabel, PW - M, 13, { align: "right" });

  barChart(doc, {
    x: M,
    y: 32,
    w: CW,
    h: 78,
    data: agg.monthly,
    title: "Monthly Sales (Grand Total)",
  });
  lineChart(doc, {
    x: M,
    y: 118,
    w: CW,
    h: 66,
    data: agg.daily,
    title: "Daily Sales Trend",
  });
  donutChart(doc, {
    x: M,
    y: 192,
    w: CW,
    h: 58,
    slices: agg.modes,
    total: totals.grand,
    title: "Payment Mode Breakdown",
  });

  // closing summary strip
  const sy = 256;
  fill(doc, COLORS.soft);
  stroke(doc, COLORS.line);
  doc.setLineWidth(0.2);
  doc.roundedRect(M, sy, CW, 20, 2, 2, "FD");
  const best = agg.monthly.length
    ? agg.monthly.reduce((m, d) => (d.value > m.value ? d : m))
    : null;
  const summary = [
    ["Bills", String(bills.length)],
    ["Net Sales", money(totals.sub)],
    ["Tax Collected", money(totals.tax)],
    ["Grand Total", money(totals.grand)],
    ["Best Month", best ? `${best.label} ${best.sub}` : "-"],
  ];
  summary.forEach((s, i) => {
    const sx = M + 4 + i * (CW / 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    ink(doc, COLORS.muted);
    doc.text(s[0].toUpperCase(), sx, sy + 7);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    ink(doc, i === 3 ? COLORS.green : COLORS.navy);
    doc.text(s[1], sx, sy + 14);
  });

  // footer on every page
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    stroke(doc, COLORS.line);
    doc.setLineWidth(0.2);
    doc.line(M, PH - 11, PW - M, PH - 11);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    ink(doc, COLORS.muted);
    doc.text(`${companyName} - Sales Report`, M, PH - 6.5);
    doc.text(`Page ${p} of ${pages}`, PW - M, PH - 6.5, { align: "right" });
  }

  doc.save(fileName);
};

// ---- public: many invoices in one PDF -----------------------------------
// `html` is a full document built with buildInvoiceDocument; every
// `.invoice-page` is captured as one A4 page (keeps Thai text & exact print look).
export const exportInvoicesPdf = async (html, fileName = "bills.pdf", onProgress) => {
  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;visibility:hidden;";
  document.body.appendChild(iframe);
  try {
    const idoc = iframe.contentDocument;
    idoc.open();
    idoc.write(html);
    idoc.close();
    // wait for images (logo) to load
    await Promise.all(
      Array.from(idoc.images).map((img) =>
        img.complete ? null : new Promise((res) => { img.onload = res; img.onerror = res; })
      )
    );

    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const PW = pdf.internal.pageSize.getWidth();
    const PH = pdf.internal.pageSize.getHeight();
    const pages = Array.from(idoc.querySelectorAll(".invoice-page"));

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        windowWidth: 794,
      });
      let w = PW - 10;
      let h = (canvas.height * w) / canvas.width;
      if (h > PH - 10) {
        h = PH - 10;
        w = (canvas.width * h) / canvas.height;
      }
      if (i > 0) pdf.addPage();
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", (PW - w) / 2, 5, w, h);
      if (onProgress) onProgress(i + 1, pages.length);
    }
    pdf.save(fileName);
  } finally {
    document.body.removeChild(iframe);
  }
};

// ---- public: purchase summary ------------------------------------------
// rows: inventory purchase records (stock_in > 0) already ordered by the caller.
export const exportPurchaseSummaryPdf = ({
  rows,
  logo,
  companyName = "StoreHub",
  periodLabel = "All dates",
  supplierLabel = "All suppliers",
  fileName = "purchase_report.pdf",
}) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 12;
  const CW = PW - M * 2;
  const amountOf = (r) => num(r.netAmount) || num(r.subtotal);

  const totals = rows.reduce(
    (a, r) => {
      a.qty += num(r.stock_in);
      a.sub += num(r.subtotal);
      a.vat += num(r.vatAmount);
      a.net += amountOf(r);
      return a;
    },
    { qty: 0, sub: 0, vat: 0, net: 0 }
  );

  // aggregation: monthly, daily, suppliers
  const monthly = {};
  const daily = {};
  const suppliers = {};
  rows.forEach((r) => {
    const d = r.purchase_date ? parseISO(String(r.purchase_date)) : null;
    const v = amountOf(r);
    if (d && isValid(d)) {
      const mk = format(d, "yyyy-MM");
      monthly[mk] = monthly[mk] || { value: 0, date: d };
      monthly[mk].value += v;
      const dk = format(d, "yyyy-MM-dd");
      daily[dk] = daily[dk] || { value: 0, date: d };
      daily[dk].value += v;
    }
    const s = r.supplier_name || "Unknown";
    suppliers[s] = suppliers[s] || { value: 0, count: 0 };
    suppliers[s].value += v;
    suppliers[s].count += 1;
  });
  const monthlyData = Object.keys(monthly)
    .sort()
    .map((k) => ({
      label: format(monthly[k].date, "MMM"),
      sub: format(monthly[k].date, "yyyy"),
      value: monthly[k].value,
    }));
  const dailyData = Object.keys(daily)
    .sort()
    .map((k) => ({ label: format(daily[k].date, "dd MMM"), value: daily[k].value }));
  const sorted = Object.entries(suppliers)
    .map(([label, v]) => ({ label, ...v }))
    .sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, 6);
  const rest = sorted.slice(6);
  if (rest.length) {
    top.push({
      label: "Others",
      value: rest.reduce((s, x) => s + x.value, 0),
      count: rest.reduce((s, x) => s + x.count, 0),
    });
  }

  // header
  fill(doc, COLORS.navy);
  doc.rect(0, 0, PW, 32, "F");
  fill(doc, COLORS.accent);
  doc.rect(0, 32, PW, 1.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(19);
  ink(doc, [255, 255, 255]);
  doc.text("Purchase Report", M, 15);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  ink(doc, [203, 213, 225]);
  doc.text(`${companyName}  |  ${supplierLabel}`, M, 22);
  doc.text(`Period: ${periodLabel}`, M, 27);
  doc.text(`Generated: ${format(new Date(), "dd MMM yyyy, HH:mm")}`, PW - M, 27, {
    align: "right",
  });
  if (logo) {
    try {
      fill(doc, [255, 255, 255]);
      doc.roundedRect(PW - M - 36, 5, 36, 14, 1.5, 1.5, "F");
      doc.addImage(logo, "PNG", PW - M - 34, 6, 32, 12);
    } catch (e) {
      // logo is optional
    }
  }

  const cw = (CW - 4 * 3) / 5;
  [
    ["Purchases", String(rows.length), COLORS.navy],
    ["Total Qty", totals.qty.toLocaleString("en-US", { maximumFractionDigits: 2 }), COLORS.blue],
    ["Subtotal", money(totals.sub), COLORS.amber],
    ["VAT", money(totals.vat), COLORS.accent],
    ["Net Amount", money(totals.net), COLORS.green],
  ].forEach((k, i) => card(doc, M + i * (cw + 3), 40, cw, 19, k[0], k[1], k[2]));

  doc.autoTable({
    startY: 66,
    margin: { left: M, right: M, bottom: 16 },
    head: [["#", "Date", "Ref No", "Item", "Supplier", "Qty", "Rate", "Subtotal", "VAT", "Net Amount"]],
    body: rows.map((r, i) => [
      i + 1,
      r.purchase_date && isValid(parseISO(String(r.purchase_date)))
        ? format(parseISO(String(r.purchase_date)), "dd-MM-yyyy")
        : "-",
      r.refno || "-",
      r.item_name || "-",
      r.supplier_name || "-",
      `${num(r.stock_in)} ${r.unit || ""}`.trim(),
      money(r.purchase_price),
      money(r.subtotal),
      money(r.vatAmount),
      money(amountOf(r)),
    ]),
    foot: [
      [
        { content: "TOTAL", colSpan: 5, styles: { halign: "right" } },
        totals.qty.toLocaleString("en-US", { maximumFractionDigits: 2 }),
        "",
        money(totals.sub),
        money(totals.vat),
        money(totals.net),
      ],
    ],
    showFoot: "lastPage",
    theme: "striped",
    styles: { fontSize: 7, cellPadding: 1.8, textColor: COLORS.text, lineColor: COLORS.line },
    headStyles: { fillColor: COLORS.navy, textColor: 255, fontStyle: "bold", halign: "center" },
    footStyles: { fillColor: COLORS.accent, textColor: 255, fontStyle: "bold", halign: "right" },
    alternateRowStyles: { fillColor: COLORS.soft },
    columnStyles: {
      0: { halign: "center", cellWidth: 8 },
      1: { halign: "center", cellWidth: 18 },
      2: { halign: "center" },
      5: { halign: "right" },
      6: { halign: "right" },
      7: { halign: "right" },
      8: { halign: "right" },
      9: { halign: "right", fontStyle: "bold", textColor: COLORS.green },
    },
  });

  // analytics page
  doc.addPage();
  fill(doc, COLORS.navy);
  doc.rect(0, 0, PW, 20, "F");
  fill(doc, COLORS.accent);
  doc.rect(0, 20, PW, 1.2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  ink(doc, [255, 255, 255]);
  doc.text("Purchase Summary & Analytics", M, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  ink(doc, [203, 213, 225]);
  doc.text(periodLabel, PW - M, 13, { align: "right" });

  barChart(doc, { x: M, y: 32, w: CW, h: 78, data: monthlyData, color: COLORS.accent, title: "Monthly Purchases (Net Amount)" });
  lineChart(doc, { x: M, y: 118, w: CW, h: 66, data: dailyData, color: COLORS.blue, title: "Daily Purchase Trend" });
  donutChart(doc, { x: M, y: 192, w: CW, h: 58, slices: top, total: totals.net, title: "Purchases by Supplier", nameHead: "SUPPLIER", countHead: "RECORDS" });

  const sy = 256;
  fill(doc, COLORS.soft);
  stroke(doc, COLORS.line);
  doc.setLineWidth(0.2);
  doc.roundedRect(M, sy, CW, 20, 2, 2, "FD");
  const best = monthlyData.length ? monthlyData.reduce((m, d) => (d.value > m.value ? d : m)) : null;
  [
    ["Purchases", String(rows.length)],
    ["Subtotal", money(totals.sub)],
    ["VAT", money(totals.vat)],
    ["Net Amount", money(totals.net)],
    ["Peak Month", best ? `${best.label} ${best.sub}` : "-"],
  ].forEach((s, i) => {
    const sx = M + 4 + i * (CW / 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    ink(doc, COLORS.muted);
    doc.text(s[0].toUpperCase(), sx, sy + 7);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    ink(doc, i === 3 ? COLORS.green : COLORS.navy);
    doc.text(s[1], sx, sy + 14);
  });

  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    stroke(doc, COLORS.line);
    doc.setLineWidth(0.2);
    doc.line(M, PH - 11, PW - M, PH - 11);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    ink(doc, COLORS.muted);
    doc.text(`${companyName} - Purchase Report`, M, PH - 6.5);
    doc.text(`Page ${p} of ${pages}`, PW - M, PH - 6.5, { align: "right" });
  }

  doc.save(fileName);
};
